import { Component } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";

// Errore di download di una pagina (code splitting): webpack lo chiama
// ChunkLoadError; con import dinamici nativi il messaggio e' "Failed to fetch
// dynamically imported module".
export function isChunkLoadError(error) {
  const text = `${error?.name || ""} ${error?.message || ""}`;
  return /ChunkLoadError|Loading (CSS )?chunk|dynamically imported module/i.test(text);
}

// Mostra un messaggio chiaro al posto di una schermata bianca quando una pagina
// non si carica o va in errore mentre viene disegnata.
//  - "Riprova" e' SEMPRE un'azione dell'utente: nessuna ricarica automatica
//    parte da qui (quella, una sola volta e limitata, e' in lib/lazy-page.js),
//    quindi non esistono cicli di ricarica.
//  - Se il guasto e' il download di una pagina, React.lazy ricorda l'esito
//    negativo e riprovare a disegnare darebbe lo stesso errore: in quel caso
//    "Riprova" ricarica la pagina.
//  - `resetKey` (il percorso) azzera l'errore quando si naviga altrove: la
//    barra laterale resta utilizzabile e un errore non resta "appiccicato".
export default class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error) {
    // Solo console: niente invio dell'errore a servizi esterni.
    console.error("[ErrorBoundary]", error);
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  retry = () => {
    if (isChunkLoadError(this.state.error)) {
      window.location.reload();
      return;
    }
    this.setState({ error: null });
  };

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    const chunk = isChunkLoadError(error);
    return (
      <div
        role="alert"
        className={`${this.props.fill || "min-h-dvh"} flex flex-col items-center justify-center gap-4 p-6 text-center`}
        data-testid="error-boundary"
      >
        <h2 className="text-xl font-bold">
          {chunk ? "Non riusciamo a caricare questa pagina" : "Qualcosa è andato storto"}
        </h2>
        <p className="max-w-md text-sm text-muted-foreground">
          {chunk
            ? "Controlla la connessione a internet e riprova. Se hai appena aggiornato l'app, ricaricare la pagina risolve il problema."
            : "Questa pagina ha avuto un problema. Riprova; se continua, ricarica la pagina o torna più tardi."}
        </p>
        <Button type="button" onClick={this.retry} data-testid="error-boundary-retry">
          Riprova
        </Button>
      </div>
    );
  }
}

// Variante per le route: si azzera da sola al cambio di percorso.
export function RouteErrorBoundary({ children, fill }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundary resetKey={pathname} fill={fill}>
      {children}
    </ErrorBoundary>
  );
}
